import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Md5Service } from '../misc/service/md5-service';
import { PositionAspectRatio, PhotoOrientation, PrintTemplateType } from './print-template.model';

/**
 * The files that ship with the panel, and the upload the create flow needs.
 *
 * A template cannot be created without artwork, so the panel provides the standard design itself and
 * uploads it on the first save. The example photos are only ever drawn in the browser — the same
 * files the backend is tested against, so a sheet coming off the printer can be held next to what
 * the editor showed.
 */
@Injectable({ providedIn: 'root' })
export class PrintTemplateAssetUploader {

    /** Absolute paths: the panel lives under /admin/..., so a relative one would resolve against it. */
    private static readonly ASSETS = '/assets/img/mock/';

    private static readonly ARTWORK: { [type: string]: string } = {
        keyring: PrintTemplateAssetUploader.ASSETS + 'keyring-template.png',
        snowglobe: PrintTemplateAssetUploader.ASSETS + 'snow-template.jpg',
    };

    /** Bypasses the interceptors, the way the shared upload zone does, for the PUT to storage. */
    private readonly storageClient: HttpClient;

    constructor(private httpClient: HttpClient, handler: HttpBackend) {
        this.storageClient = new HttpClient(handler);
    }

    /** Where the shipped artwork lives, so a draft can show it before anything is uploaded. */
    artworkUrl(type: PrintTemplateType): string {
        return PrintTemplateAssetUploader.ARTWORK[ type ] || PrintTemplateAssetUploader.ARTWORK.keyring;
    }

    /**
     * A stand-in guest photo in the ratio being laid out. The red band along its top edge is the
     * point: on a printed sheet it says immediately whether the photo came out the right way up.
     */
    examplePhotoUrl(aspectRatio: PositionAspectRatio, orientation: PhotoOrientation): string {
        return PrintTemplateAssetUploader.ASSETS + 'example-' + aspectRatio + '-' + orientation + '.jpg';
    }

    /**
     * Standard artwork for the product, uploaded as the template's background. The CMS and the admin
     * panel upload through different endpoints, so the caller says where.
     */
    async uploadArtwork(campaignRef: string, type: PrintTemplateType, uploadUrl?: string): Promise<any> {
        const path = this.artworkUrl(type);
        const blob = await (await fetch(path)).blob();

        return this.upload(campaignRef, blob, path.split('/').pop(), 'keyring', '3x2', uploadUrl);
    }

    /**
     * The three step indirect upload: ask for a signed url, put the file on storage outside the API,
     * then register it.
     */
    private async upload(campaignRef: string, blob: Blob, name: string,
                         relatedTo: string, aspectRatio: string, uploadUrl?: string): Promise<any> {
        const url = uploadUrl || '/admin/campaign/' + campaignRef + '/file/upload';
        const extension = name.split('.').pop();

        const signed: any = await this.httpClient.post<any>(url, { extension: extension }).toPromise();

        await this.storageClient.put(signed.signedUrl, blob, {
            headers: new HttpHeaders({ 'Content-Type': blob.type || 'image/jpeg' }),
        }).toPromise();

        const md5sum = await Md5Service.compute(<any>blob);

        return this.httpClient.put<any>(url, {
            ...signed,
            originalFileName: name,
            fileType: 'image',
            extension: extension,
            md5sum: md5sum,
            relatedTo: relatedTo,
            aspectRatio: aspectRatio,
        }).toPromise();
    }
}
